import { Autocomplete, Box, Button, Grid, IconButton, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { ElementItemProps } from "../types";
import { useDevice } from "../../../../utils/DeviceProvider";
import { useEditedColor } from "../../../../hooks/useEditedStyles";
import { useIntl } from "react-intl";

import ClearIcon from "@mui/icons-material/Clear";
import UndoIcon from "@mui/icons-material/Undo";
import { isEqual } from "lodash-es";
import ElementFrame from "./ElementFrame";
import { DbRemotefileForList } from "@helios/toolbox-types";

export type RemoteFile = DbRemotefileForList;

export interface REMOTEFILEParameters {
  remotefiles: RemoteFile[];
}

const sortArray = (a: number, b: number) => {
  return a - b;
};

const REMOTEFILE = ({ remotefiles, configKey, disabled }: Omit<ElementItemProps<REMOTEFILEParameters>, "elementKey" | "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<RemoteFile[] | null>(null);
  const [value, setValue] = useState<RemoteFile[] | null>(null);
  const [filteredRemotefiles, setFilteredRemotefiles] = useState<RemoteFile[] | null>(null);

  const autocompleteInputRef = useRef<HTMLDivElement | null>(null);

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { formatMessage } = useIntl();
  const { background: editedBackground, hover: editedHover, text: editedText } = useEditedColor();

  const onChange = useCallback(
    (value: RemoteFile[]) => {
      if (configIndex === null) {
        return;
      }
      currentValue[configKey].items[configIndex].value = value;
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  const handleSelect = useCallback(
    (value: RemoteFile) => {
      autocompleteInputRef.current?.blur();
      setValue((old) => {
        const v = [...(old ?? []), value];
        onChange(v);
        return v;
      });
    },
    [onChange]
  );

  const handleRemove = useCallback(
    (removeId: number) => {
      setValue((old) => {
        if (!old) {
          return old;
        }
        const index = old.findIndex(({ id }) => id === removeId);
        old.splice(index, 1);

        const v = [...old];
        onChange(v);
        return v;
      });
    },
    [onChange]
  );

  const hasRemoved = useCallback((saved: RemoteFile[] | null, value: RemoteFile[]) => {
    const v = value.map(({ id }) => id);

    return (saved ?? []).filter(({ id }) => !v.includes(id)).length > 0;
  }, []);

  const orderChanged = useCallback((saved: RemoteFile[] | null, value: RemoteFile[]) => {
    if ((saved ?? []).length !== value.length) {
      return false;
    }

    const s = (saved ?? []).map(({ id }) => id);
    const v = value.map(({ id }) => id);

    return !isEqual(s, v) && isEqual(s.sort(sortArray), v.sort(sortArray));
  }, []);

  const resetValue = useCallback(() => {
    if (!config || configIndex === null) {
      return;
    }
    const saved = config.toolboxConfig[configKey].items[configIndex].value;
    currentValue[configKey].items[configIndex].value = saved;
    setCurrentValue(currentValue);
  }, [config, configIndex, configKey, currentValue, setCurrentValue]);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type }: { type: string }) => type === "REMOTEFILE") ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex]?.value ?? null;
    setSaved(svd);
    const current = currentValue[configKey].items[configIndex]?.value ?? [];
    setValue([...current]);
  }, [active, currentValue, config, configIndex, configKey, remotefiles]);

  useEffect(() => {
    if (!value || configIndex === null) {
      return;
    }
    setFilteredRemotefiles(remotefiles?.filter(({ id }) => !value.map(({ id }: { id: number }) => id).includes(id)));
  }, [value, remotefiles, currentValue, configIndex, configKey]);

  if (value === null || filteredRemotefiles === null) {
    return (
      <Grid container spacing={1}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Grid item key={`skeleton-${index}`}>
            <Skeleton variant="rectangular" height={40} sx={{ width: 120, borderRadius: 1 }} />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={40} sx={{ width: 300, borderRadius: 1 }} />
        </Grid>
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  const edited = !isEqual(saved, value);

  return (
    <Grid container spacing={1}>
      {value.map((file) => (
        <Grid item key={`file-${file.id}-${file.filename}`}>
          <RemoteFileItem
            remoteFile={file}
            onRemove={disabled ? undefined : handleRemove}
            border
            edited={!(saved ?? []).map(({ id }) => id).includes(file.id)}
          />
        </Grid>
      ))}
      {orderChanged(saved, value) ? (
        <Grid item xs={12}>
          <Box sx={{ backgroundColor: editedBackground, color: editedText, borderRadius: 1, padding: 1, display: "inline-block" }}>
            <Typography component="span">{formatMessage({ defaultMessage: "Order has changed." })}</Typography>
          </Box>
        </Grid>
      ) : null}
      <Grid item xs={12} container spacing={1} sx={{ alignItems: "center" }}>
        <Grid item>
          <Autocomplete
            disablePortal
            options={filteredRemotefiles}
            getOptionLabel={({ filename, ver: version, size, unitid, client_code }) =>
              [
                filename,
                formatMessage({ defaultMessage: "version: {version}, size: {size} B" }, { version, size }),
                unitid ? unitid : client_code === null ? formatMessage({ defaultMessage: "global" }) : "",
              ].join(" ")
            }
            noOptionsText={formatMessage({ defaultMessage: "No options" })}
            renderOption={(props, remoteFile) => (
              <MenuItem
                {...props}
                sx={{
                  padding: 0,
                  ...((saved ?? []).map(({ id }) => id).includes(remoteFile.id)
                    ? { backgroundColor: editedBackground, "&.MuiMenuItem-root:hover": { backgroundColor: editedHover } }
                    : {}),
                }}
                onClick={() => handleSelect(remoteFile)}
              >
                <RemoteFileItem remoteFile={remoteFile} />
              </MenuItem>
            )}
            sx={{ width: 300, ...(hasRemoved(saved, value) ? { backgroundColor: editedBackground } : {}) }}
            renderInput={(params) => (
              <TextField
                inputRef={autocompleteInputRef}
                {...params}
                label={formatMessage({ defaultMessage: "Add new file" })}
                variant="filled"
                sx={{ margin: 0 }}
                size="small"
              />
            )}
            disabled={disabled}
          />
        </Grid>
        {edited ? (
          <Grid item>
            <Button startIcon={<UndoIcon />} color="warning" sx={{ alignSelf: "flex-start" }} onClick={resetValue} disabled={disabled}>
              {formatMessage({ defaultMessage: "Reset to saved value" })}
            </Button>
          </Grid>
        ) : null}

        <Grid item>
          <ElementFrame label={""} />
        </Grid>
      </Grid>
    </Grid>
  );
};

const RemoteFileItem = ({
  remoteFile,
  onRemove,
  border,
  edited,
}: {
  remoteFile: RemoteFile;
  onRemove?: (id: number) => void;
  border?: boolean;
  edited?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { background: editedBackground, text: editedText } = useEditedColor();

  return (
    <Box
      sx={{
        ...(border ? { borderWidth: 1, borderStyle: "solid", borderColor: (theme) => theme.palette.divider, borderRadius: 1 } : {}),
        ...(edited ? { backgroundColor: editedBackground, borderColor: editedText } : {}),
        ...(remoteFile.id === -1 ? { borderColor: (theme) => theme.palette.error.dark } : {}),
      }}
    >
      <Grid container spacing={1} sx={{ flexDirection: "row", flexWrap: "nowrap", alignItems: "center", padding: border ? 1 : 0 }}>
        <Grid item container sx={{ flexDirection: "column" }}>
          <Grid item>
            <Typography variant="subtitle2">{remoteFile.filename}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" sx={{ color: (theme) => theme.palette.secondary.main }}>
              {formatMessage({ defaultMessage: "version: {version}, size: {size} B" }, { version: remoteFile.ver, size: remoteFile.size })}
            </Typography>
          </Grid>
          {remoteFile.client_code === null || remoteFile.unitid !== null ? (
            <Grid item>
              <Typography variant="body2" sx={{ color: (theme) => theme.palette.warning.main }}>
                {remoteFile.unitid ?? formatMessage({ defaultMessage: "global" })}
              </Typography>
            </Grid>
          ) : null}
          {remoteFile.id === -1 ? (
            <Grid item>
              <Typography variant="body2" sx={{ color: (theme) => theme.palette.error.main }}>
                {formatMessage({ defaultMessage: "File has been removed!" })}
              </Typography>
            </Grid>
          ) : null}
        </Grid>
        {onRemove ? (
          <Grid item>
            <IconButton onClick={() => onRemove(remoteFile.id)}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default REMOTEFILE;
